import type { ExerciseTemplate } from '@yuan-math/exercises';
import { fraction, roundTo, toLatex } from '@yuan-math/math';
import {
  choiceExercise,
  expressionExercise,
  fractionExercise,
  numericExercise,
  options,
  template,
  trap,
  within,
} from './authoring.ts';

/** Decimals, measurement, geometry and introductory algebra. */
export const APPLIED_TEMPLATES: ExerciseTemplate[] = [
  /* --------------------------------------------------------------- decimals */
  template({
    id: 'decimal-digit-value',
    conceptId: 'decimal-place-value',
    description: 'State the value of a digit after the decimal point.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const places = difficulty === 2 ? 2 : 3;
      const whole = rng.int(1, 9);
      const decimals = Array.from({ length: places }, () => rng.int(1, 9));
      const index = rng.int(0, places - 1);
      const digit = decimals[index] as number;
      const value = roundTo(digit / 10 ** (index + 1), 4);
      const names = ['tenths', 'hundredths', 'thousandths'];
      const number = `${whole}.${decimals.join('')}`;
      return numericExercise({
        prompt: `In ${number}, what is the value of the digit ${digit} in the ${names[index]} place?`,
        figure: { kind: 'place-value', digits: `${whole}${decimals.join('')}`, places: ['Ones', ...names.slice(0, places)] },
        value,
        summary: `It sits in the ${names[index]} place, so it is worth ${digit}/${10 ** (index + 1)} = ${value}.`,
        hint: 'After the point the places are tenths, hundredths, thousandths.',
        steps: [
          { label: 'Name the place.', latex: `\\text{${names[index]}}` },
          { label: 'Write it as a fraction.', latex: `\\frac{${digit}}{${10 ** (index + 1)}}` },
          { label: 'As a decimal.', latex: String(value) },
        ],
        traps: [trap(digit, 'place-value-shift')],
      });
    },
  }),

  template({
    id: 'decimal-compare',
    conceptId: 'compare-decimals',
    description: 'Compare two decimals — the "more digits is bigger" trap.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      // Deliberately pair a short decimal against a longer, smaller one.
      const short = roundTo(rng.int(1, 9) / 10, 2);
      const long = roundTo(rng.int(10, 99) / 100, 2);
      const whole = difficulty === 3 ? rng.int(1, 5) : 0;
      const a = roundTo(whole + short, 2);
      const b = roundTo(whole + long, 2);
      if (a === b) {
        return numericExercise({
          prompt: `Which is larger, ${a} or ${b + 0.01}?`,
          value: roundTo(b + 0.01, 2),
          summary: 'Compare place by place from the left.',
        });
      }
      const { choices, correctId } = options(
        [
          { label: String(a), correct: a > b, ...(a < b ? { misconceptionId: 'decimal-longer-is-larger' } : {}) },
          { label: String(b), correct: b > a, ...(b < a ? { misconceptionId: 'decimal-longer-is-larger' } : {}) },
        ],
        rng.shuffle,
      );
      return choiceExercise({
        prompt: 'Which decimal is larger?',
        choices,
        correctId,
        summary: `Padding to the same length: ${a.toFixed(2)} and ${b.toFixed(2)}. Compare place by place.`,
        hint: 'Give both numbers the same number of decimal places by adding zeros, then compare.',
        steps: [
          { label: 'Pad with zeros.', latex: `${a.toFixed(2)} \\text{ and } ${b.toFixed(2)}` },
          { label: 'Compare the tenths first.', latex: `${Math.floor(a * 10) % 10} \\text{ vs } ${Math.floor(b * 10) % 10}` },
          { label: 'Larger.', latex: String(Math.max(a, b)) },
        ],
      });
    },
  }),

  template({
    id: 'decimal-add',
    conceptId: 'add-decimals',
    description: 'Add or subtract two decimals.',
    difficulties: [2, 3, 4],
    build: ({ rng, difficulty }) => {
      const scale = difficulty === 2 ? 10 : 100;
      const a = roundTo(rng.int(10, difficulty === 4 ? 999 : 99) / scale + rng.int(0, 20), 2);
      const b = roundTo(rng.int(10, difficulty === 4 ? 999 : 99) / scale, 2);
      const subtracting = difficulty >= 3 && rng.bool();
      const value = roundTo(subtracting ? a - b : a + b, 2);
      return numericExercise({
        prompt: subtracting ? 'Subtract.' : 'Add.',
        latex: `${a} ${subtracting ? '-' : '+'} ${b}`,
        value,
        // Slack for binary floating point, not an accuracy allowance: at these
        // magnitudes it is four orders of magnitude below a hundredth, so no
        // wrong answer to two decimal places can slip through it.
        tolerance: within(1e-6, value),
        summary: `Line up the decimal points and work column by column: ${value}.`,
        hint: 'Write the numbers with the decimal points underneath each other, padding with zeros.',
        steps: [
          { label: 'Line up the decimal points.', latex: `${a.toFixed(2)} ${subtracting ? '-' : '+'} ${b.toFixed(2)}` },
          { label: 'Work column by column from the right.', latex: String(value) },
        ],
        traps: [trap(roundTo(value * 10, 2), 'place-value-shift'), trap(roundTo(value / 10, 3), 'place-value-shift')],
      });
    },
  }),

  template({
    id: 'decimal-as-fraction',
    conceptId: 'decimal-place-value',
    description: 'Write a decimal as a fraction.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const places = difficulty === 3 ? 1 : 2;
      const numerator = rng.int(1, 10 ** places - 1);
      const denominator = 10 ** places;
      const value = roundTo(numerator / denominator, places);
      return fractionExercise({
        prompt: 'Write this decimal as a fraction in lowest terms.',
        latex: String(value),
        value: fraction(numerator, denominator),
        requireSimplified: true,
        summary: `${value} is ${numerator}/${denominator}, which simplifies to ${toLatex(fraction(numerator, denominator)).replace(/\\frac\{(\d+)\}\{(\d+)\}/, '$1/$2')}.`,
        hint: `There ${places === 1 ? 'is one digit' : 'are two digits'} after the point, so the denominator is ${denominator}.`,
        steps: [
          { label: 'Count the decimal places.', latex: `${places} \\Rightarrow \\text{denominator } ${denominator}` },
          { label: 'Write the fraction.', latex: `\\frac{${numerator}}{${denominator}}` },
          { label: 'Simplify.', latex: toLatex(fraction(numerator, denominator)) },
        ],
      });
    },
  }),

  /* -------------------------------------------------------------- geometry */
  template({
    id: 'rectangle-perimeter',
    conceptId: 'perimeter',
    description: 'Find the perimeter of a rectangle.',
    difficulties: [1, 2, 3],
    build: ({ rng, difficulty }) => {
      const length = rng.int(2, difficulty === 1 ? 9 : difficulty === 2 ? 15 : 40);
      const width = rng.int(2, difficulty === 1 ? 9 : difficulty === 2 ? 15 : 40);
      return numericExercise({
        prompt: `A rectangle is ${length} cm long and ${width} cm wide. What is its perimeter?`,
        figure: { kind: 'shape', shape: 'rectangle', labels: { width: `${length} cm`, height: `${width} cm` } },
        unit: 'cm',
        value: 2 * (length + width),
        summary: `Perimeter = 2 × (${length} + ${width}) = ${2 * (length + width)} cm.`,
        hint: 'Walk around the outside: length, width, length, width.',
        steps: [
          { label: 'Add a length and a width.', latex: `${length} + ${width} = ${length + width}` },
          { label: 'Double it, since there are two of each.', latex: `2 \\times ${length + width} = ${2 * (length + width)}` },
        ],
        traps: [trap(length * width, 'area-for-perimeter'), trap(length + width, 'regrouping-error')],
      });
    },
  }),

  template({
    id: 'rectangle-area',
    conceptId: 'area-rectangles',
    description: 'Find the area of a rectangle.',
    difficulties: [1, 2, 3],
    build: ({ rng, difficulty }) => {
      const length = rng.int(2, difficulty === 1 ? 8 : difficulty === 2 ? 12 : 25);
      const width = rng.int(2, difficulty === 1 ? 8 : difficulty === 2 ? 12 : 25);
      return numericExercise({
        prompt: `A rectangle is ${length} cm long and ${width} cm wide. What is its area?`,
        figure:
          length <= 12 && width <= 12
            ? { kind: 'grid', width: length, height: width }
            : { kind: 'shape', shape: 'rectangle', labels: { width: `${length} cm`, height: `${width} cm` } },
        unit: 'cm²',
        value: length * width,
        summary: `Area = ${length} × ${width} = ${length * width} cm².`,
        hint: 'Count how many unit squares fit in one row, then how many rows.',
        steps: [
          { label: 'Multiply length by width.', latex: `${length} \\times ${width}` },
          { label: 'Area, in square units.', latex: `${length * width}\\text{ cm}^2` },
        ],
        traps: [trap(2 * (length + width), 'perimeter-for-area'), trap(length + width, 'perimeter-for-area')],
      });
    },
  }),

  template({
    id: 'rectangle-missing-side',
    conceptId: 'area-rectangles',
    description: 'Find a missing side length from the area.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const width = rng.int(3, 12);
      const length = rng.int(3, 15);
      const area = length * width;
      return numericExercise({
        prompt: `A rectangle has an area of ${area} cm² and a width of ${width} cm. How long is it?`,
        unit: 'cm',
        value: length,
        summary: `Area = length × width, so length = ${area} ÷ ${width} = ${length} cm.`,
        hint: 'Area is length times width, so divide the area by the side you know.',
        steps: [
          { label: 'Start from the area formula.', latex: `${area} = \\ell \\times ${width}` },
          { label: 'Divide both sides by the known width.', latex: `\\ell = \\frac{${area}}{${width}} = ${length}` },
        ],
        traps: [trap(area * width, 'divide-wrong-direction'), trap(area - width, 'perimeter-for-area')],
      });
    },
  }),

  /* --------------------------------------------------------------- algebra */
  template({
    id: 'evaluate-expression',
    conceptId: 'variables',
    description: 'Evaluate an algebraic expression at a given value.',
    difficulties: [2, 3, 4],
    build: ({ rng, difficulty }) => {
      const coefficient = rng.int(2, 9);
      const constant = rng.int(1, difficulty === 2 ? 9 : 20);
      const x = rng.int(2, difficulty === 2 ? 6 : 12);
      const subtracting = difficulty >= 3 && rng.bool();
      const value = subtracting ? coefficient * x - constant : coefficient * x + constant;
      return numericExercise({
        prompt: `Evaluate the expression when x = ${x}.`,
        latex: `${coefficient}x ${subtracting ? '-' : '+'} ${constant}`,
        value,
        summary: `Substituting x = ${x}: ${coefficient} × ${x} = ${coefficient * x}, ${subtracting ? 'minus' : 'plus'} ${constant} gives ${value}.`,
        hint: `Replace x with ${x}, then follow the order of operations.`,
        steps: [
          { label: 'Substitute.', latex: `${coefficient}(${x}) ${subtracting ? '-' : '+'} ${constant}` },
          { label: 'Multiply first.', latex: `${coefficient * x} ${subtracting ? '-' : '+'} ${constant}` },
          { label: 'Then add or subtract.', latex: String(value) },
        ],
        traps: [
          trap(subtracting ? (coefficient - constant) * x : (coefficient + constant) * x, 'multiply-then-add-wrong-order'),
          trap(Number(`${coefficient}${x}`), 'variable-as-label'),
        ],
      });
    },
  }),

  template({
    id: 'write-expression',
    conceptId: 'variables',
    description: 'Write an algebraic expression from a description.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const coefficient = rng.int(2, 9);
      const constant = rng.int(1, 12);
      const scenario = rng.pick([
        { text: `A taxi charges £${constant} to start, plus £${coefficient} for each kilometre. Write an expression for the cost of n kilometres.`, canonical: `${coefficient}*n + ${constant}` },
        { text: `A box holds ${coefficient} pencils. Write an expression for the number of pencils in n boxes, plus ${constant} loose ones.`, canonical: `${coefficient}*n + ${constant}` },
      ]);
      return expressionExercise({
        prompt: scenario.text,
        canonical: scenario.canonical,
        summary: `Each unit contributes ${coefficient}, so that is ${coefficient}n, and ${constant} is added once.`,
        hint: 'Which part changes with n, and which part is fixed?',
        steps: [
          { label: 'The part that depends on n.', latex: `${coefficient}n` },
          { label: 'The fixed part.', latex: String(constant) },
          { label: 'Put them together.', latex: `${coefficient}n + ${constant}` },
        ],
      });
    },
  }),

  template({
    id: 'one-step-equation',
    conceptId: 'one-step-equations',
    description: 'Solve a one-step equation.',
    difficulties: [2, 3, 4],
    build: ({ rng, difficulty }) => {
      const x = rng.int(2, difficulty === 2 ? 9 : 20);
      const operation = rng.pick(difficulty === 2 ? (['add', 'subtract'] as const) : (['add', 'subtract', 'multiply', 'divide'] as const));
      const k = rng.int(2, difficulty === 2 ? 9 : 12);

      const setups = {
        add: { latex: `x + ${k} = ${x + k}`, inverse: `subtract ${k}`, work: `${x + k} - ${k}` },
        subtract: { latex: `x - ${k} = ${x - k}`, inverse: `add ${k}`, work: `${x - k} + ${k}` },
        multiply: { latex: `${k}x = ${k * x}`, inverse: `divide by ${k}`, work: `\\frac{${k * x}}{${k}}` },
        divide: { latex: `\\frac{x}{${k}} = ${x}`, inverse: `multiply by ${k}`, work: `${x} \\times ${k}` },
      } as const;
      const setup = setups[operation];
      const answer = operation === 'divide' ? x * k : x;

      return numericExercise({
        prompt: 'Solve for x.',
        latex: setup.latex,
        value: answer,
        summary: `Undo the operation by doing the opposite to both sides: x = ${answer}.`,
        hint: `x has been changed by ${k}. What undoes that?`,
        steps: [
          { label: `To isolate x, ${setup.inverse} on both sides.`, latex: setup.work },
          { label: 'Simplify.', latex: `x = ${answer}` },
          { label: 'Check by substituting back.', latex: setup.latex.replace('x', `(${answer})`) },
        ],
        traps: [
          trap(operation === 'add' ? x + 2 * k : operation === 'subtract' ? x - 2 * k : answer * k, 'variable-as-label'),
        ],
      });
    },
  }),
];
